import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { NetworkTreeWebComponent } from '../component/NetworkTreeWebComponent';
import { NetworkTreeRepository } from '../repository/NetworkTreeRepository';
import { NetworkTreeView } from '../view/NetworkTreeView';
import { templateNetworkTreeContainer } from '../view/template';
import {
  EntitiesNotAllowedInTree,
  NetworkTreeEvent,
  NetworkTreeRenderData,
  NetworkTreeStructure,
  ProtectedEntityWithChildren,
  TreeDetails,
} from '../types';
import { NodeData, TreeEvent } from '../../../ui/gzTree/type';
import { transformProtectedEntityToNodeData, treePrefixRenderer } from '../helpers/tree.helper';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { TreeSearchEvent } from '../../networkTreeSearch/types';
import { elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { NetworkTreeDataSource } from '../dataProvider/NetworkTreeDataSource';

export class NetworkTreeController extends BaseWebComponentController {
  public component: NetworkTreeWebComponent;
  public dataRepository: NetworkTreeRepository;
  public view: NetworkTreeView;
  public viewConstructor: typeof NetworkTreeView;
  public dataRepositoryConstructor: typeof NetworkTreeRepository;
  private activeNodeFound = false;

  constructor(component: NetworkTreeWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: NetworkTreeView,
      viewTemplate: templateNetworkTreeContainer,
      dataRepositoryConstructor: NetworkTreeRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$wrapper,
        event: TreeEvent.CHANGED_ACTIVE_NODE,
        callback: this.handleNodeClicked,
      },
      {
        element: this.view.dom.$searchTree,
        event: TreeSearchEvent.START_FETCHING,
        callback: this.hiddenTree,
      },
      {
        element: this.view.dom.$searchTree,
        event: TreeSearchEvent.INPUT_RESET,
        callback: this.handleResetSearchTree,
      },
      {
        element: this.view.dom.$searchTree,
        event: TreeSearchEvent.ITEM_CLICKED,
        callback: this.handleSearchTreeItemClicked,
      },
      {
        element: this.view.dom.$wrapper,
        event: TreeEvent.NODE_TOGGLED,
        callback: this.handleNodeToggled,
      },
      {
        element: this.view.dom.$wrapper,
        event: NetworkTreeEvent.TREE_LOADED,
        callback: this.handleTreeLoaded,
      },
    ];
  }

  private handleSearchTreeItemClicked(event: CustomEvent) {
    const data: ProtectedEntity = event.detail.protectedEntity as ProtectedEntity;
    // should set activeId and tree details to the tree
    this.component.propertiesSet = false;
    this.component.treeDetails = {
      targetId: data._id,
      type: data.type,
      path: data.path,
      parentId: data.parentId,
      name: data.name,
      specifics: data.specifics,
      companyId: data.company?.id,
    };

    this.emitNodeChangedEvent();
  }

  private handleResetSearchTree(event: CustomEvent) {
    if (event.detail.shouldReconstructTree) {
      this.component.propertiesSet = true;
    } else {
      this.showTree();
    }
    this.component.classList.remove('hidden');
  }

  public async getDataForView(): Promise<NetworkTreeRenderData> {
    this.view.removeOldTreeIfPresent();

    if (this.view.dom.$loader.classList.contains('hidden')) {
      this.view.dom.$loader.classList.remove('hidden');
    }

    this.component.rootNode = await this.dataRepository.getRootNode();

    const rootNode = {
      ...this.component.rootNode,
      namePrefixRenderer: () => treePrefixRenderer(this.component.rootNode.originalData, true),
    };
    const dataSource = new NetworkTreeDataSource(this.dataRepository.dataProvider.getOptions());
    dataSource.options.rootNodeIds = [rootNode.nodeId];

    const treeDetails = this.component.treeDetails;
    // if undefined/rootNode fallback to default company id
    if (treeDetails === undefined || treeDetails.targetId === rootNode.nodeId) {
      return await this.setRootNodeActive(dataSource, rootNode);
    }

    let treeStructure: NetworkTreeStructure;

    try {
      // tree details provided
      treeStructure = await this.dataRepository.getTreeNodesForChild(treeDetails, rootNode);
    } catch (error) {
      const renderData = await this.setRootNodeActive(dataSource, rootNode);
      this.emitNodeChangedEvent();
      return renderData;
    }

    this.activeNodeFound = false;
    await this.setRecursiveTree(dataSource, rootNode, treeStructure.data, treeDetails);

    // There are some cases when the parent of the given node will not be found. In this case the selected node
    // should be the parent of the deepest child. Ex: See the protectedEntity 'EPS' folder that is hidden from network
    if (!this.activeNodeFound) {
      const node = this.findParentOfDeepestChild(treeStructure.data) || { ...rootNode.originalData };
      if (!node) {
        console.error('Could not find any target to set the active tree node with.');
        return {};
      }

      const nodeData: NodeData = {
        nodeId: node._id,
        nodeName: node.name,
        originalData: node,
        namePrefixRenderer: () => treePrefixRenderer(node),
      };
      await dataSource.toggleNodeOpen(nodeData, true);
      dataSource.setNodeActive(nodeData);

      this.component.treeDetails = {
        targetId: node._id,
        path: node.path,
        type: node.type,
        parentId: node.parentId,
        name: node.name,
        specifics: node.specifics,
      };
    }

    if (this.component.findParentNode) {
      this.emitNodeChangedEvent();
    }

    return {
      dataSource,
      hasActions: this.component.hasActions,
    };
  }

  private async setRootNodeActive(
    dataSource: NetworkTreeDataSource,
    rootNode: NodeData
  ): Promise<NetworkTreeRenderData> {
    this.component.treeDetails = this.getActiveTargetInfoFromNode(rootNode);
    const rootNodeData = await this.dataRepository.getNodeChildren(rootNode);

    dataSource.setNodes(rootNodeData.currentNode, rootNodeData.childNodes);
    dataSource.setNodeActive(rootNodeData.currentNode);

    return {
      dataSource,
      hasActions: this.component.hasActions,
    };
  }

  private findParentOfDeepestChild(tree: Array<ProtectedEntityWithChildren>): ProtectedEntity | null {
    if (!tree) return null;

    let maxDepth = -1;
    let parentOfDeepest = null;

    function traverse(node, depth, parent) {
      if (!node) return;

      if (depth > maxDepth && (!node.data || node.data.length === 0)) {
        maxDepth = depth;
        parentOfDeepest = parent;
      }

      if (node.data) {
        for (const child of node.data) {
          traverse(child, depth + 1, node);
        }
      }
    }

    for (const child of tree) {
      traverse(child, 0, null);
    }

    return parentOfDeepest;
  }

  public getActiveTargetInfoFromNode(node: NodeData): TreeDetails {
    const peInfo = node.originalData as ProtectedEntity;
    return {
      targetId: peInfo._id,
      type: peInfo.type,
      path: peInfo.path,
      parentId: peInfo.parentId,
      name: peInfo.name,
      specifics: peInfo.specifics,
      companyId: peInfo.company?.id,
    };
  }

  public isRootNode(protectedEntityId: string): boolean {
    return this.component.rootNode?.nodeId === protectedEntityId;
  }

  private async setRecursiveTree(
    dataSource: NetworkTreeDataSource,
    parent: NodeData,
    children: Array<ProtectedEntityWithChildren>,
    activeNodeInfo: TreeDetails
  ) {
    dataSource.setNodes(
      {
        ...parent,
        isOpen: children.length !== 0,
      },
      children
        .filter(child => !EntitiesNotAllowedInTree.includes(child.type))
        .map(child => ({
          nodeId: child._id,
          nodeName: child.name,
          namePrefixRenderer: () => treePrefixRenderer(child),
          loadedPage: child.page,
        }))
    );

    if (!parent.childNodeIds && children.length) {
      parent.childNodeIds = children
        .filter(child => !EntitiesNotAllowedInTree.includes(child.type))
        .map(child => child._id);
    }

    if (
      (parent.nodeId === activeNodeInfo.targetId && !this.component.findParentNode) ||
      (parent.nodeId === activeNodeInfo.parentId && this.component.findParentNode)
    ) {
      if (this.isRootNode(parent.nodeId) && !parent.isOpen) {
        await dataSource.toggleNodeOpen(parent, true);
      }

      this.activeNodeFound = true;

      if (parent.originalData.type !== Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER) {
        const useCacheOption = !parent.childNodeIds?.length;
        await dataSource.toggleNodeOpen(parent, true, useCacheOption);
      }

      dataSource.setNodeActive(parent);

      this.component.treeDetails = {
        targetId: parent.originalData._id,
        path: parent.originalData.path,
        type: parent.originalData.type,
        parentId: parent.originalData.parentId,
        name: parent.originalData.name,
        specifics: parent.originalData.specifics,
        companyId: parent.originalData.company?.id,
      };
    }

    if (!children || children.length === 0) {
      return;
    }

    children.map(child =>
      this.setRecursiveTree(dataSource, transformProtectedEntityToNodeData(child), child.data || [], activeNodeInfo)
    );
  }

  private handleNodeClicked(event: CustomEvent) {
    const peInfo = event.detail.originalData as ProtectedEntity;
    this.component.treeDetails = {
      targetId: peInfo._id,
      type: peInfo.type,
      path: peInfo.path,
      parentId: peInfo.parentId,
      name: peInfo.name,
      specifics: peInfo.specifics,
      companyId: peInfo.company?.id,
    };

    event.preventDefault();

    this.emitNodeChangedEvent();
  }

  private emitNodeChangedEvent() {
    this.component.dispatchEvent(
      new CustomEvent(NetworkTreeEvent.NODE_CHANGED, {
        detail: {
          nodeInfo: this.component.treeDetails,
          fullTreeNodesMap: this.view.dom.$tree?.treeNodesMap,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private hiddenTree() {
    const tree = this.view.dom.$wrapper.querySelector('gz-tree');
    // return if not found
    if (!tree) {
      return;
    }

    elementClassListAdd(tree, 'hidden');
  }

  private showTree() {
    const tree = this.view.dom.$wrapper.querySelector('gz-tree');
    // return if not found
    if (!tree) {
      return;
    }

    elementClassListRemove(tree, 'hidden');
    tree.scrollToNode(this.component.treeDetails.targetId);
  }

  private handleNodeToggled(event: CustomEvent) {
    const node = event.detail as NodeData;
    if (node.isOpen !== true) {
      return;
    }

    const nodeId = node.nodeId;
    const activeNodeId = this.component.treeDetails.targetId;
    const treeDataSource = this.view.dom.$tree.dataSource;
    const originalNode = treeDataSource.nodes.get(nodeId) as NodeData;
    const activeNodeIsChildOfCurrentNode = originalNode.childNodeIds?.find(id => id === activeNodeId) !== undefined;

    if (activeNodeIsChildOfCurrentNode) {
      const activeNode = treeDataSource.nodes.get(activeNodeId) as NodeData;
      treeDataSource.setNodeActive(activeNode);
    }
  }

  private handleTreeLoaded(): void {
    const tree = this.view.dom.$wrapper.querySelector('gz-tree');
    if (!tree) {
      return;
    }
    // Small delay to ensure complete layout stabilization
    setTimeout(() => {
      // The delay creates a clean break in the JavaScript execution queue
      // allowing all layout and paint operations to complete
      tree.scrollToNode(this.component.treeDetails.targetId);
    }, 10);
  }
}
